import React, { FC, useCallback } from 'react'
import { Box, Button, DialogActions, DialogTitle, Typography } from '@mui/material'
import ContentCopy from '@mui/icons-material/ContentCopy'
import { Dialog } from 'src/components'
import { useAddPopupMessage } from 'src/hooks/ui'

interface NotFoundSensorsDialogProps {
  open: boolean
  onClose: () => void
  // Identifiers that matched no sensor (result.notFound).
  identifiers: string[]
  // Total distinct identifiers the user searched for (the "Y" in "X of Y").
  total: number
}

export const NotFoundSensorsDialog: FC<NotFoundSensorsDialogProps> = ({ open, onClose, identifiers, total }) => {
  const addPopupMessage = useAddPopupMessage()

  const handleCopy = useCallback(async () => {
    const text = identifiers.join('\n')
    try {
      await navigator.clipboard.writeText(text)
      addPopupMessage({ text: 'Copied to clipboard', type: 'success' })
    } catch {
      addPopupMessage({ text: 'Unable to copy to clipboard', type: 'error' })
    }
  }, [identifiers, addPopupMessage])

  // Call the close handler with no arguments. MUI Dialog's `onClose` and the button's `onClick`
  // both pass an event object, and the underlying `useDialog().closeDialog` forwards its first
  // argument into `setTimeout(cb, 200)` — forwarding the event there makes setTimeout eval the
  // stringified event (`[object Object]`) as code, throwing a SyntaxError.
  const handleClose = useCallback(() => onClose(), [onClose])

  if (identifiers.length === 0) return null

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={<DialogTitle>{`The following sensors could not be found (${identifiers.length} of ${total})`}</DialogTitle>}
      actions={
        <DialogActions>
          <Button variant="outlined" startIcon={<ContentCopy />} onClick={handleCopy}>
            Copy
          </Button>
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      }
    >
      <Box
        sx={{
          minWidth: { xs: 'auto', sm: 360 },
          maxWidth: '100%',
          maxHeight: 320,
          overflow: 'auto',
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'action.hover',
          fontFamily: 'monospace',
          userSelect: 'text',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all'
        }}
      >
        {identifiers.map((identifier) => (
          <Typography key={identifier} variant="body2" component="div" sx={{ fontFamily: 'inherit' }}>
            {identifier}
          </Typography>
        ))}
      </Box>
    </Dialog>
  )
}
